const DEFAULT_CSS = 1;
const DEFAULT_MSS = 1;

const AEROBIC_ZONE_FACTORS = {
    100: {
        rec: 0.94,
        end1: 0.96,
        end2: 0.99,
        end3: 1.025
    },
    200: {
        rec: 0.92,
        end1: 0.94,
        end2: 0.97,
        end3: 1.005
    },
    300: {
        rec: 0.91,
        end1: 0.93,
        end2: 0.96,
        end3: 0.995
    },
    400: {
        rec: 0.9,
        end1: 0.92,
        end2: 0.95,
        end3: 0.985
    }
};

const ANAEROBIC_ZONE_FACTORS = {
    sp1: 0.925,
    sp2: 0.975,
    sp3: 1.05
};

const AEROBIC_ZONES = new Set(['rec', 'end1', 'end2', 'end3']);

function toPositiveNumber(value, fallback) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
}

export function getAerobicReferenceDistance(distance) {
    const normalizedDistance = toPositiveNumber(distance, 0);

    if (normalizedDistance <= 100) return 100;
    if (normalizedDistance <= 200) return 200;
    if (normalizedDistance <= 300) return 300;
    return 400;
}

export function getRepSwimTimeSeconds(distance, zone, { css, mss } = {}) {
    const normalizedDistance = toPositiveNumber(distance, 0);
    if (!normalizedDistance || !zone) {
        return 0;
    }

    if (AEROBIC_ZONES.has(zone)) {
        const referenceDistance = getAerobicReferenceDistance(normalizedDistance);
        const zoneFactor = AEROBIC_ZONE_FACTORS[referenceDistance]?.[zone];
        if (!zoneFactor) {
            return 0;
        }

        const normalizedCss = toPositiveNumber(css, DEFAULT_CSS);
        return normalizedDistance / (normalizedCss * zoneFactor);
    }

    const zoneFactor = ANAEROBIC_ZONE_FACTORS[zone];
    if (!zoneFactor) {
        return 0;
    }

    const normalizedCss = toPositiveNumber(css, DEFAULT_CSS);
    const normalizedMss = toPositiveNumber(mss, normalizedCss || DEFAULT_MSS);
    return normalizedDistance / (normalizedMss * zoneFactor);
}

export function calculateProgramItemTime(item, { parentSet, css, mss } = {}) {
    const isSubItem = Boolean(item?._isSubItem) || parentSet !== undefined;
    const reps = Math.max(1, toPositiveNumber(item?.reps, 1));
    const sets = isSubItem
        ? Math.max(1, toPositiveNumber(parentSet, 1))
        : Math.max(1, toPositiveNumber(item?.set, 1));

    const swimming =
        getRepSwimTimeSeconds(item?.distance, item?.zones, { css, mss }) * reps * sets;

    const repsRestBase = Math.max(0, reps - 1) * toPositiveNumber(item?.repsRest, 0);
    const repsRest = isSubItem ? repsRestBase * sets : repsRestBase;
    const setRest = isSubItem
        ? 0
        : Math.max(0, sets - 1) * toPositiveNumber(item?.setRest, 0);
    const afterSetRest = isSubItem
        ? toPositiveNumber(item?.afterSetRest, 0) * sets
        : toPositiveNumber(item?.afterSetRest, 0);

    const rest = repsRest + setRest + afterSetRest;

    return {
        swimming,
        rest,
        total: swimming + rest
    };
}

export function formatDuration(seconds, precision = 2) {
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return '0:00';
    }

    let minutes = Math.floor(seconds / 60);
    let wholeSeconds = Math.floor(seconds % 60);
    const fractionBase = 10 ** precision;
    let fraction = Math.round((seconds - Math.floor(seconds)) * fractionBase);

    if (fraction === fractionBase) {
        fraction = 0;
        wholeSeconds += 1;
    }

    if (wholeSeconds === 60) {
        wholeSeconds = 0;
        minutes += 1;
    }

    if (precision <= 0) {
        return `${minutes}:${wholeSeconds.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${wholeSeconds
        .toString()
        .padStart(2, '0')}.${fraction.toString().padStart(precision, '0')}`;
}
